import { useState } from 'react';
import { CategoryIcon } from '@/components/category-icon/CategoryIcon';
import { cn } from '@/lib/utils';
import type { AssetNote } from '@/features/self-service/api/types';

interface ProductThumbProps {
  urls: string[];
  notes: AssetNote[];
  category: string;
  alt: string;
  className?: string;
  /**
   * A legenda já é lida por extenso ao lado (carrinho, pedido): a tarja vira
   * só visual, senão o leitor de tela diz "Cor diferente" duas vezes seguidas.
   */
  noteDescribedNearby?: boolean;
}

/**
 * Primeira foto da peça, com a legenda de procedência quando ela existe.
 *
 * A legenda **não é opcional**: nem toda foto é a foto daquela peça ("Cor
 * diferente", "Modelo aproximado"), e omiti-la venderia a imagem como sendo do
 * produto. Fica na base, na largura toda — canto é onde o olho não vai, e este
 * é o caso em que a pessoa mais precisa ler antes de decidir.
 *
 * As URLs apontam para o site do fornecedor, então foto quebrada é caso normal:
 * a falha cai no ícone da categoria em vez de um retângulo vazio.
 */
export function ProductThumb({
  urls,
  notes,
  category,
  alt,
  className,
  noteDescribedNearby = false,
}: ProductThumbProps) {
  const [failed, setFailed] = useState(false);
  const url = urls.find((candidate) => candidate.length > 0);
  const note = url ? notes.find((n) => n.url === url)?.note ?? null : null;

  return (
    <div
      className={cn(
        'relative aspect-[3/4] w-full overflow-hidden rounded-md bg-muted',
        className
      )}
    >
      {url && !failed ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-muted-foreground">
          <CategoryIcon category={category} size={40} aria-hidden />
        </span>
      )}
      {note && (
        <span
          aria-hidden={noteDescribedNearby || undefined}
          className="pointer-events-none absolute inset-x-0 bottom-0 bg-foreground/90 px-2 py-1 text-center text-[11px] font-semibold leading-tight text-background">
          {note}
        </span>
      )}
    </div>
  );
}
